import { KNOWLEDGE_BASE_AGENT_ID } from "@repo/app-types/entities/configurationAgent";
import type { KnowledgeBaseAttachment } from "@repo/app-types/entities/knowledgeBase";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { Size } from "@repo/ui/helpers/helpers";
import { useConfirmationModal } from "@repo/ui/hooks/useConfirmationModal";
import { Label } from "@repo/ui/Label/label";
import { PageHeader } from "@repo/ui/Navigation/pageHeader";
import { IconChevronLeft, IconTrash } from "@tabler/icons-react";
import {
  type FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDeleteKnowledgeBaseAttachmentMutation } from "../../../api/entities/knowledgeBase.js";
import { useAppDispatch } from "../../../hooks/hook.js";
import { AttachmentEditorContent } from "../../agents/components/attachmentEditorContent.js";
import { useAttachmentEditorState } from "../../agents/components/useAttachmentEditorState.js";
import { MastraChatProvider } from "../../agents/mastra/mastraChatContext.js";
import { useMastraChat } from "../../agents/mastra/useMastraChat.js";
import {
  NotificationType,
  showNotification,
} from "../../notificationsOverlay/notificationsSlice.js";
import { KnowledgeBaseAssistantChat } from "./knowledgeBaseAssistant/knowledgeBaseAssistantChat.js";
import { KnowledgeBaseAssistantContextProvider } from "./knowledgeBaseAssistant/knowledgeBaseAssistantContext.js";
import {
  buildInitialAssistantMessage,
  usePrefillFromUrl,
} from "./knowledgeBaseAssistant/knowledgeBaseAssistantPrefill.js";
import { KnowledgeBaseSyncPill } from "./knowledgeBaseSyncPill.js";

interface KnowledgeBaseAttachmentEditorProps {
  knowledgeBaseId: string;
  attachmentId: string;
  /** All attachments in the knowledge base, for document name resolution */
  knowledgeBaseAttachments?: readonly KnowledgeBaseAttachment[];
  /** Known read-only state from the parent knowledge base while the document loads. */
  isReadOnly?: boolean;
  onClose: () => void;
}

export const KnowledgeBaseAttachmentEditor: FC<
  KnowledgeBaseAttachmentEditorProps
> = (props) => {
  const {
    knowledgeBaseId,
    attachmentId,
    knowledgeBaseAttachments,
    isReadOnly,
    onClose,
  } = props;
  const dispatch = useAppDispatch();

  // Pre-fill from URL params (when redirected from a finding feedback)
  const prefill = usePrefillFromUrl();
  const initialMessages = useMemo(
    () => (prefill ? [buildInitialAssistantMessage(prefill)] : undefined),
    [prefill],
  );

  const chat = useMastraChat({
    agentId: KNOWLEDGE_BASE_AGENT_ID,
    initialMessages,
    onSend: async () => ({
      requestContext: {
        knowledgeBaseId,
        currentAttachmentId: attachmentId,
      },
    }),
  });

  const {
    attachment,
    isLoading,
    isSaving,
    isInitialized,
    editedBody,
    setEditedBody,
    fetchedBody,
    hasUnsavedChanges,
    onSave,
    onDiscard,
    selectedTabIndex,
    setSelectedTabIndex,
  } = useAttachmentEditorState({ knowledgeBaseId, attachmentId });

  // Pending patch from AI assistant
  const [pendingPatch, setPendingPatch] = useState<{
    find: string;
    replace: string;
  } | null>(null);

  // Apply pending patch once content is loaded (same logic as useAttachmentEditor)
  const appliedPatchRef = useRef<{ find: string; replace: string } | null>(
    null,
  );

  useEffect(() => {
    if (
      pendingPatch &&
      isInitialized &&
      appliedPatchRef.current !== pendingPatch
    ) {
      appliedPatchRef.current = pendingPatch;
      let body = editedBody;

      const idx = body.indexOf(pendingPatch.find);
      if (idx !== -1) {
        body =
          body.substring(0, idx) +
          pendingPatch.replace +
          body.substring(idx + pendingPatch.find.length);
      }

      setEditedBody(body);
      setSelectedTabIndex(1); // Switch to "Diff" tab
      setPendingPatch(null);
    }
  }, [
    pendingPatch,
    isInitialized,
    editedBody,
    setEditedBody,
    setSelectedTabIndex,
  ]);

  const handleApplyPatch = useCallback(
    (patch: { find: string; replace: string }) => {
      setPendingPatch(patch);
    },
    [],
  );

  const [deleteAttachment, { isLoading: isDeleting }] =
    useDeleteKnowledgeBaseAttachmentMutation();

  const handleSave = useCallback(async () => {
    try {
      await onSave();
      showNotification(dispatch, {
        id: `attachment-save-${Date.now().toString()}`,
        type: NotificationType.SUCCESS,
        primaryMessage: "Article saved",
        secondaryMessage: "Your changes have been saved.",
      });
    } catch {
      showNotification(dispatch, {
        id: `attachment-save-error-${Date.now().toString()}`,
        type: NotificationType.FAILURE,
        primaryMessage: "Failed to save article",
        secondaryMessage: "Please try again.",
      });
    }
  }, [onSave, dispatch]);

  const handleDelete = useCallback(async () => {
    try {
      await deleteAttachment({ knowledgeBaseId, attachmentId }).unwrap();
      showNotification(dispatch, {
        id: `delete-success-${attachmentId}`,
        primaryMessage: `Successfully deleted "${attachment?.title || attachment?.file_name || ""}" document`,
        type: NotificationType.SUCCESS,
      });
      onClose();
    } catch (err: any) {
      showNotification(dispatch, {
        id: `delete-error-${attachmentId}`,
        primaryMessage: `Failed to delete document: ${err.data?.error || err.message || "Unknown error"}`,
        type: NotificationType.FAILURE,
      });
    }
  }, [
    deleteAttachment,
    knowledgeBaseId,
    attachmentId,
    attachment,
    dispatch,
    onClose,
  ]);

  const {
    renderConfirmationModal: renderDiscardModal,
    onConfirmThenClose: onDiscardThenClose,
  } = useConfirmationModal({
    needsConfirmation: hasUnsavedChanges,
    onClose,
  });

  const {
    renderConfirmationModal: renderDeleteModal,
    onConfirmThenClose: onConfirmThenDelete,
  } = useConfirmationModal({
    needsConfirmation: true,
    onClose: handleDelete,
  });

  // Keep the state unknown until either the document or its parent KB has
  // loaded. Treating undefined as editable flashes the AI assistant while a
  // synced document is in flight.
  const isSynced = attachment ? Boolean(attachment.sync) : isReadOnly;
  const documentTitle = attachment
    ? attachment.title || attachment.file_name || "Untitled document"
    : undefined;

  return (
    <div className="flex h-full flex-col">
      {renderDiscardModal({
        title: "Discard changes?",
        message:
          "You have unsaved changes. Are you sure you want to discard them?",
        confirmText: "Discard",
      })}
      {renderDeleteModal({
        title: "Delete document?",
        message:
          "Are you sure you want to delete this document? This action cannot be undone.",
        confirmText: "Delete",
      })}
      <PageHeader
        className="pb-3"
        title={documentTitle}
        isLoading={!documentTitle}
        leadingAction={
          <IconButton
            accessibleLabel="Back to documents"
            variant={ButtonVariant.Tertiary}
            onClick={onDiscardThenClose}
            size={Size.SMALL}
            icon={<IconChevronLeft aria-hidden className="size-4" />}
          />
        }
        actions={
          attachment?.sync ? (
            <KnowledgeBaseSyncPill
              provider={attachment.sync.provider}
              lastSyncedAt={attachment.sync.synced_at}
            />
          ) : isSynced === false ? (
            <div className="flex items-center gap-2">
              {hasUnsavedChanges && (
                <Button
                  variant={ButtonVariant.Secondary}
                  onClick={onDiscard}
                  size={Size.SMALL}
                >
                  Discard changes
                </Button>
              )}
              <Button
                variant={ButtonVariant.Primary}
                onClick={handleSave}
                isLoading={isSaving}
                isDisabled={isSaving || !hasUnsavedChanges}
                size={Size.SMALL}
              >
                Save changes
              </Button>
              <IconButton
                accessibleLabel="Delete document"
                variant={ButtonVariant.Secondary}
                onClick={onConfirmThenDelete}
                isDisabled={isDeleting}
                size={Size.SMALL}
                icon={<IconTrash className="size-4 text-destructive" />}
              />
            </div>
          ) : undefined
        }
      />
      <div className="flex min-h-0 flex-1">
        <AttachmentEditorContent
          attachment={attachment}
          isLoading={isLoading}
          isSaving={isSaving}
          isReadOnly={isSynced}
          showTitle={false}
          editedBody={editedBody}
          savedBody={fetchedBody}
          setEditedBody={setEditedBody}
          selectedTabIndex={selectedTabIndex}
          onSelectedTabIndexChange={setSelectedTabIndex}
        />
        {isSynced === false ? (
          <MastraChatProvider chat={chat}>
            <KnowledgeBaseAssistantContextProvider
              onApplyPatch={handleApplyPatch}
              knowledgeBaseAttachments={knowledgeBaseAttachments}
            >
              <div className="flex h-full w-[380px] flex-col border-l border-border-light bg-background">
                <div className="border-b border-border-light bg-white px-4 py-3">
                  <Label size={Size.SMALL}>AI Assistant</Label>
                </div>
                <div className="flex min-h-0 flex-1 flex-col">
                  <KnowledgeBaseAssistantChat />
                </div>
              </div>
            </KnowledgeBaseAssistantContextProvider>
          </MastraChatProvider>
        ) : null}
      </div>
    </div>
  );
};
